import { DIFFICULTY, type Difficulty, type Technique } from '../config/constants';

/**
 * Local personal-best score for the arcade modes, kept **per (technique × difficulty)** (#43):
 * a cut/easy best and a thrust/xhard best never share a record. Stored outside `UserSettings`
 * so a settings reset never wipes it; the cloud leaderboard owns the shared copy separately.
 * `custom` runs never record (no comparable board), so they read/write nothing.
 */
function keyFor(technique: Technique, difficulty: Difficulty): string {
  return `arcadeBest_${technique}_${difficulty}`;
}

export function loadArcadeBest(technique: Technique, difficulty: Difficulty): number {
  if (difficulty === DIFFICULTY.custom) return 0;
  try {
    const raw = localStorage.getItem(keyFor(technique, difficulty));
    const n = raw == null ? 0 : Number(raw);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

export function saveArcadeBest(score: number, technique: Technique, difficulty: Difficulty): void {
  if (difficulty === DIFFICULTY.custom) return;
  try {
    localStorage.setItem(keyFor(technique, difficulty), String(Math.max(0, Math.floor(score))));
  } catch (err) {
    console.warn('saveArcadeBest failed', err);
  }
}

/**
 * Persist `score` if it beats the stored best for `(technique, difficulty)`. Returns true when a
 * new record was set. Custom runs never record.
 */
export function recordArcadeScore(
  score: number,
  technique: Technique,
  difficulty: Difficulty,
): boolean {
  if (difficulty === DIFFICULTY.custom) return false;
  if (score > loadArcadeBest(technique, difficulty)) {
    saveArcadeBest(score, technique, difficulty);
    return true;
  }
  return false;
}
